import {
  StaticImage,
  GatsbyImage,
  getImage,
  IGatsbyImageData,
} from 'gatsby-plugin-image'
import React, { Fragment, useEffect, useState } from 'react'
import { Head } from '@components'
import { LazyImageFull } from 'react-lazy-images'
import Lightbox from 'lightbox-react'
import * as st from '@assets/styl/enterprisesHanded.module.styl'
import 'lightbox-react/style.css'
import cn from 'classnames'
import { useLanguage } from '../../hooks'
import { translatePath } from '../../translations'
import { graphql, useStaticQuery } from 'gatsby'

type ImageFile = {
  childImageSharp: { gatsbyImageData: IGatsbyImageData }
} | null

type Zoom = {
  index?: number
  items?: Array<string>
}

type ItemType = {
  facade: string
  name: string
  neighborhood: string
  city: string
  url: string
  images: Array<string>
  imageFiles: Array<ImageFile>
  year: number | string
  handed: string
  gallery: Array<string>
}

interface Props extends ItemType {
  zoom: (o: Zoom) => void
}

const OptimizedImage = ({
  file,
  fallbackSrc,
  alt,
}: {
  file: ImageFile
  fallbackSrc: string
  alt: string
}) => {
  const image = getImage(file || undefined)
  return image ? (
    <GatsbyImage image={image} alt={alt} />
  ) : (
    <img src={fallbackSrc} alt={alt} loading="lazy" />
  )
}

const Item = ({
  name,
  images,
  imageFiles,
  facade,
  neighborhood,
  city,
  url,
  handed,
  year,
  gallery,
  zoom,
}: Props) => {
  const handedParts = (handed || `Entregue em ${year}`).split('Entregue em ')
  const { t } = useLanguage()
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <div className={st.item}>
      <div className={st.description}>
        <div className={st.container}>
          <div className={st.descriptionContent}>
            <div className={st.info}>
              <h5>{name}</h5>
              <ul>
                <li>
                  {neighborhood} <span>/</span> {city}
                </li>
                {handedParts && (
                  <li>
                    {t('HandedOut.deliveredTo')}
                    <span></span> {handedParts}
                  </li>
                )}
              </ul>
            </div>
            {!isMobile && (
              <div className={st.linkButton}>
                {url && (
                  <a
                    className="btn"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('HandedOut.visitWebsite')}
                  </a>
                )}
                {Boolean(gallery && gallery.length) && (
                  <button
                    className={'btn'}
                    onClick={() => {
                      zoom({ index: 0, items: gallery })
                    }}
                  >
                    {t('HandedOut.photoGallery')}
                  </button>
                )}
              </div>
            )}
          </div>
          <div>
            {images[0] && (
              <div className={st.facade}>
                <OptimizedImage
                  file={imageFiles?.[0]}
                  fallbackSrc={images[0]}
                  alt="Imagem Entregue 01"
                />
              </div>
            )}
          </div>
        </div>
        <div className={st.largeImage}>
          {images[1] && (
            <div className={st.facadeTwo}>
              <OptimizedImage
                file={imageFiles?.[1]}
                fallbackSrc={images[1]}
                alt="Imagem Entregue 02"
              />
            </div>
          )}
        </div>
        {isMobile && (
          <div className={st.linkButton}>
            {url && (
              <a
                className="btn"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('HandedOut.visitWebsite')}
              </a>
            )}
            {Boolean(gallery && gallery.length) && (
              <button
                className={'btn'}
                onClick={() => {
                  zoom({ index: 0, items: gallery })
                }}
              >
                {t('HandedOut.photoGallery')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const HandedOutPage = () => {
  const defaultZoomObj: Zoom = {}
  const [zoomObj, setZoomObj] = useState(defaultZoomObj)

  const items: Array<ItemType> = useStaticQuery(graphql`
    query {
      allHanded {
        nodes {
          facade
          name
          neighborhood
          city
          url
          images
          year
          handed
          gallery
          imageFiles {
            childImageSharp {
              gatsbyImageData(placeholder: BLURRED)
            }
          }
        }
      }
    }
  `).allHanded.nodes

  let years = []
  for (const item of items) {
    if (!years.includes(item.year)) years.push(item.year)
  }
  years.sort().reverse()

  const { t } = useLanguage()

  return (
    <>
      <Head
        pathname={translatePath('/empreendimentos/entregues/')}
        basePath="/empreendimentos/entregues/"
        title={t('HandedOut.titlePage')}
        description={t('HandedOut.descriptionPage')}
      />

      <section className={cn(st.hero, 'heroScroll')}>
        <h1>
          {t('HandedOut.title.1')} <b>{t('HandedOut.title.2')}</b>
        </h1>
        <div className={st.bg}>
          <StaticImage
            src="../../assets/img/entregues-hero.jpg"
            alt=""
            className={st.desktop}
            loading="lazy"
          />
          <StaticImage
            src="../../assets/img/entregues-hero-mobile.jpg"
            alt=""
            className={st.mobile}
            loading="lazy"
          />
        </div>
      </section>

      <section className={st.core}>
        <div>
          <h3>{t('HandedOut.subtitle')}</h3>

          {years.map((year, key) => (
            <Fragment key={key}>
              <h4>{year}</h4>
              {items
                .filter((item) => item.year === year)
                .map((item, key) => (
                  <Item key={key} {...item} zoom={setZoomObj} />
                ))}
            </Fragment>
          ))}
        </div>
      </section>

      {zoomObj.items && (
        <Lightbox
          mainSrc={zoomObj.items[zoomObj.index]}
          onCloseRequest={() => setZoomObj({})}
          prevSrc={
            zoomObj.items[
              (zoomObj.index + zoomObj.items.length - 1) % zoomObj.items.length
            ]
          }
          nextSrc={zoomObj.items[(zoomObj.index + 1) % zoomObj.items.length]}
          onMovePrevRequest={() =>
            setZoomObj({
              ...zoomObj,
              index:
                (zoomObj.index + zoomObj.items.length - 1) %
                zoomObj.items.length,
            })
          }
          onMoveNextRequest={() =>
            setZoomObj({
              ...zoomObj,
              index: (zoomObj.index + 1) % zoomObj.items.length,
            })
          }
        />
      )}
    </>
  )
}

export default HandedOutPage
